import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { Flex, Paragraph } from "@/atoms"
import { RESOURCES_NAMES } from "@/const/resource.constants"
import { useCreate } from "@/hooks/useHttp"
import { useLogger } from "@/hooks/useLogger"
import { Heading } from "@/molecules/Heading"
import { ErrorResult } from "@/organisms/ErrorBoundary"
import { IntegrationStatusIcon } from "@/pages/IntegrationsPage/components/molecules/IntegrationStatusIcon"
import { Loader } from "@/pages/SignInPage/components/molecules/Loader"

import styles from "./CliAuth.module.scss"

type CliCallbackResponse = { token: string; state: string; port: number }

const submitCallbackForm = (action: string, fields: Record<string, string>) => {
  const form = document.createElement("form")
  form.method = "POST"
  form.action = action
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }
  document.body.appendChild(form)
  form.submit()
}

export const CliAuth = () => {
  const [searchParams] = useSearchParams()
  const state = searchParams.get("state")
  const port = searchParams.get("port")
  const result = searchParams.get("result")
  const isResult = !!result

  const { logger } = useLogger()
  const [isError, setError] = useState(!state || !port)

  const callback = useCreate<
    CliCallbackResponse,
    { state: string; port: number }
  >(RESOURCES_NAMES.AUTHENTICATION.CLI_CALLBACK)

  useEffect(() => {
    if (isResult) return
    if (!state || !port) {
      logger.error(new Error("Missing CLI auth parameters"), {
        tags: { state: !!state, port: !!port, flow: "cli-oauth" }
      })
      return
    }
    if (!callback.isIdle) return

    callback
      .mutateAsync({ state, port: Number(port) })
      .then((response) => {
        if (!response?.token) {
          throw new Error("No token in response")
        }
        submitCallbackForm(`http://localhost:${response.port}/callback`, {
          token: response.token,
          state: response.state
        })
      })
      .catch((err) => {
        logger.error(err instanceof Error ? err : new Error(String(err)), {
          tags: { flow: "cli-oauth", action: "callback" }
        })
        setError(true)
      })
  }, [state, port, callback, logger])

  if (isResult) {
    const isSuccess = result === "success"

    return (
      <Flex
        vertical
        align="center"
        justify="center"
        gap={20}
        className={styles.wrapper}
      >
        <IntegrationStatusIcon
          variant="xl"
          integrationStatus={isSuccess ? "SUCCESS" : "ERROR"}
        />
        <Flex gap={12} vertical align="center" justify="center">
          <Heading
            level={2}
            title={`Authentication ${isSuccess ? "Successful" : "Failed"}`}
          />
          <Paragraph>
            {isSuccess
              ? "You can close this tab and return to your terminal."
              : "Please return to your terminal and try again."}
          </Paragraph>
        </Flex>
      </Flex>
    )
  }

  if (isError) return <ErrorResult placement="LAYOUT" errorType="Unknown" />
  return <Loader />
}
